import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { useQueryClient } from 'react-query';
import api from '../services/api.ts';
import { formatCurrency, applyCurrencyMask, parseCurrency } from '../utils/currency.ts';
import { ArrowLeftIcon, ArrowRightIcon, PlusIcon, XMarkIcon, MagnifyingGlassIcon, CheckIcon } from '@heroicons/react/24/outline';

type EntityType = 'asset' | 'vehicle' | 'aircraft' | 'vessel';

interface ExistingItem { id: number; label: string; type: EntityType; value?: number; assetType?: 'real_estate' | 'other' }

interface ResultItemAsset {
  entityType: EntityType;
  payload: any;
  display?: Record<string, string>;
}

const ExchangeRegister: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [selectedOrigins, setSelectedOrigins] = useState<ExistingItem[]>([]);
  const [results, setResults] = useState<ResultItemAsset[]>([]);
  const [exchangeDate, setExchangeDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [totalValue, setTotalValue] = useState<number>(0);
  const [notes] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const { data: assets } = useQuery<any[]>(['assets'], async () => (await api.get('/assets')).data);
  const { data: vehicles } = useQuery<any[]>(['vehicles'], async () => (await api.get('/vehicles')).data);
  const { data: aircrafts } = useQuery<any[]>(['aircrafts'], async () => (await api.get('/aircrafts')).data);
  const { data: vessels } = useQuery<any[]>(['vessels'], async () => (await api.get('/vessels')).data);

  const exchangeMutation = useMutation(async () => {
    const payload = {
      exchangeDate,
      origins: selectedOrigins.map(o => ({ entityType: o.type, entityId: o.id })),
      results: results.map(r => ({ entityType: r.entityType, payload: r.payload })),
    };
    const res = await api.post('/exchanges', payload);
    return res.data;
  }, {
    onSuccess: (data: any) => {
      queryClient.invalidateQueries('assets');
      queryClient.invalidateQueries('vehicles');
      queryClient.invalidateQueries('aircrafts');
      queryClient.invalidateQueries('vessels');
      queryClient.invalidateQueries('asset-transactions');
      selectedOrigins.forEach(o => {
        if (o.type === 'asset') queryClient.invalidateQueries(['related-assets', String(o.id)]);
        if (o.type === 'vehicle') queryClient.invalidateQueries(['related-vehicle', String(o.id)]);
        if (o.type === 'aircraft') queryClient.invalidateQueries(['related-aircraft', String(o.id)]);
        if (o.type === 'vessel') queryClient.invalidateQueries(['related-vessel', String(o.id)]);
      });
      const resultAssets = Array.isArray(data?.results) ? data.results.filter((r: any) => r.entityType === 'asset') : [];
      resultAssets.forEach((r: any) => {
        queryClient.invalidateQueries(['related-assets', String(r.entityId)]);
      });
      const resultVehicles = Array.isArray(data?.results) ? data.results.filter((r: any) => r.entityType === 'vehicle') : [];
      resultVehicles.forEach((r: any) => {
        queryClient.invalidateQueries(['related-vehicle', String(r.entityId)]);
      });
      const resultAircrafts = Array.isArray(data?.results) ? data.results.filter((r: any) => r.entityType === 'aircraft') : [];
      resultAircrafts.forEach((r: any) => {
        queryClient.invalidateQueries(['related-aircraft', String(r.entityId)]);
      });
      const resultVessels = Array.isArray(data?.results) ? data.results.filter((r: any) => r.entityType === 'vessel') : [];
      resultVessels.forEach((r: any) => {
        queryClient.invalidateQueries(['related-vessel', String(r.entityId)]);
      });
      navigate(-1);
    }
  });

  useEffect(() => {
    const sum = results.reduce((s, r) => {
      if (r.entityType === 'asset') return s + Number(r.payload?.currentValue || 0);
      if (r.entityType === 'vehicle') return s + Number(r.payload?.sellingPrice || 0);
      if (r.entityType === 'aircraft') return s + Number(r.payload?.sellingPrice || 0);
      if (r.entityType === 'vessel') return s + Number(r.payload?.sellingPrice || 0);
      return s;
    }, 0);
    setTotalValue(sum);
    // Display do total removido da UI; mantemos cálculo para cartões
  }, [results]);

  const optionalValue = (obj: any, keys: string[]): number => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null) return Number(v || 0);
    }
    return 0;
  };

  const availableItems: ExistingItem[] = useMemo(() => {
    const assetItems = (assets || [])
      .filter(a => String(a.status) === 'active')
      .map(a => ({ id: Number(a.id), label: a.description, type: 'asset' as EntityType, value: optionalValue(a, ['currentValue', 'acquisitionValue']), assetType: String(a.type) === 'real_estate' ? 'real_estate' : 'other' } as any));
    const vehicleItems = (vehicles || [])
      .filter(v => String(v.status) === 'in_stock')
      .map(v => ({ id: Number(v.id), label: `${v.make} ${v.model}`, type: 'vehicle' as EntityType, value: optionalValue(v, ['sellingPrice', 'purchasePrice']) } as any));
    const aircraftItems = (aircrafts || [])
      .filter(a => String(a.status) === 'in_stock')
      .map(a => ({ id: Number(a.id), label: `${a.make} ${a.model}`, type: 'aircraft' as EntityType, value: optionalValue(a, ['sellingPrice', 'purchasePrice']) } as any));
    const vesselItems = (vessels || [])
      .filter(v => String(v.status) === 'in_stock')
      .map(v => ({ id: Number(v.id), label: `${v.make} ${v.model}`, type: 'vessel' as EntityType, value: optionalValue(v, ['sellingPrice', 'purchasePrice']) } as any));
    return [...assetItems, ...vehicleItems, ...aircraftItems, ...vesselItems];
  }, [assets, vehicles, aircrafts, vessels]);

  const filteredItems = useMemo(() => {
    const t = searchText.trim().toLowerCase();
    if (!t) return availableItems;
    return availableItems.filter(i => `${i.label}`.toLowerCase().includes(t));
  }, [availableItems, searchText]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const originType = (params.get('originType') || '').toLowerCase() as EntityType;
    const originIdRaw = params.get('originId');
    const originId = originIdRaw ? Number(originIdRaw) : null;
    if (originType && originId) {
      const match = availableItems.find(i => i.type === originType && i.id === originId);
      if (match) {
        setSelectedOrigins(prev => (prev.some(o => o.type === match.type && o.id === match.id) ? prev : [...prev, match]));
      }
    }
  }, [location.search, availableItems]);

  const addResult = (type: EntityType) => {
    const base: ResultItemAsset = { entityType: type, payload: {}, display: {} };
    if (type === 'asset') base.payload = { description: '', type: 'other', acquisitionValue: 0, currentValue: 0, acquisitionDate: exchangeDate, details: {} };
    if (type === 'vehicle') base.payload = { make: '', model: '', year: new Date().getFullYear(), licensePlate: '', color: '', purchasePrice: 0, sellingPrice: 0, purchaseDate: exchangeDate };
    if (type === 'aircraft') base.payload = { make: '', model: '', year: new Date().getFullYear(), tailNumber: '', color: '', purchasePrice: 0, sellingPrice: 0, purchaseDate: exchangeDate, category: 'other' };
    if (type === 'vessel') base.payload = { make: '', model: '', year: new Date().getFullYear(), registrationNumber: '', color: '', purchasePrice: 0, sellingPrice: 0, purchaseDate: exchangeDate, category: 'other' };
    setResults(prev => [...prev, base]);
  };

  const removeResult = (index: number) => setResults(prev => prev.filter((_, i) => i !== index));

  const updateResult = (index: number, path: string, value: any) => {
    setResults(prev => prev.map((r, i) => i === index ? { ...r, payload: { ...r.payload, [path]: value } } : r));
  };

  const updateResultCurrency = (index: number, path: string, rawInput: string) => {
    const masked = applyCurrencyMask(rawInput);
    const numeric = parseCurrency(masked);
    setResults(prev => prev.map((r, i) => (
      i === index
        ? { ...r, payload: { ...r.payload, [path]: numeric }, display: { ...(r.display || {}), [path]: masked } }
        : r
    )));
  };

  const cededTotal = useMemo(() => selectedOrigins.reduce((s, o: any) => s + Number(o.value || 0), 0), [selectedOrigins]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors">
            <ArrowLeftIcon className="h-5 w-5 mr-2" /> Voltar
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Registrar Permuta</h1>
          <p className="text-gray-600 mt-2">Selecione ativos e cadastre os recebidos</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Ativos existentes envolvidos</h3>
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(o => !o)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 flex items-center justify-between"
              >
                <span className="text-sm text-gray-700">Selecionar ativos</span>
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
              </button>
              {dropdownOpen && (
                <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      value={searchText}
                      onChange={e => setSearchText(e.target.value)}
                      placeholder="Buscar por nome"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {filteredItems.map((item: any) => {
                      const selected = selectedOrigins.some(o => o.type === item.type && o.id === item.id);
                      const bracket = item.type === 'asset' ? (item.assetType === 'real_estate' ? 'Imóvel' : 'Patrimônio') : item.type === 'vehicle' ? 'Veículo' : item.type === 'aircraft' ? 'Aeronave' : 'Embarcação';
                      return (
                        <button
                          key={`${item.type}-${item.id}`}
                          onClick={() => {
                            setSelectedOrigins(prev => selected
                              ? prev.filter(o => !(o.type === item.type && o.id === item.id))
                              : [...prev, item]
                            );
                          }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${selected ? 'bg-purple-50' : ''}`}
                        >
                          <span>[{bracket}] {item.label}</span>
                          <span className="text-gray-600">{formatCurrency(Number(item.value || 0))}</span>
                          {selected && <CheckIcon className="h-4 w-4 text-purple-600" />}
                        </button>
                      );
                    })}
                  </div>
                  <div className="p-2 flex justify-end">
                    <button onClick={() => setDropdownOpen(false)} className="text-sm px-3 py-1 rounded bg-gray-100 hover:bg-gray-200">Fechar</button>
                  </div>
                </div>
              )}
            </div>

            {selectedOrigins.length > 0 && (
              <div className="mt-4 space-y-2">
                {selectedOrigins.map((o: any, idx) => (
                  <div key={`${o.type}-${o.id}-${idx}`} className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2">
                    <div className="text-sm text-gray-700">[{o.assetType ? (o.assetType === 'real_estate' ? 'Imóvel' : 'Patrimônio') : (o.type === 'vehicle' ? 'Veículo' : o.type === 'aircraft' ? 'Aeronave' : 'Embarcação')}] {o.label}</div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">{formatCurrency(Number(o.value || 0))}</span>
                      <button onClick={() => setSelectedOrigins(prev => prev.filter(p => !(p.type === o.type && p.id === o.id)))} className="text-red-600 text-xs">Remover</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Informações da Permuta</h3>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data</label>
            <input type="date" value={exchangeDate} onChange={e => setExchangeDate(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-3 mb-6" />
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500">Cedidos</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCurrency(cededTotal)}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500">Recebidos</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCurrency(totalValue)}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500">Saldo</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCurrency(totalValue - cededTotal)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">Novos ativos recebidos</h3>
            <div className="flex gap-2">
              <button onClick={() => addResult('asset')} className="bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-sm flex items-center"><PlusIcon className="h-4 w-4 mr-1"/>Adicionar Patrimônio</button>
              <button onClick={() => addResult('vehicle')} className="bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-sm flex items-center"><PlusIcon className="h-4 w-4 mr-1"/>Adicionar Veículo</button>
              <button onClick={() => addResult('aircraft')} className="bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-sm flex items-center"><PlusIcon className="h-4 w-4 mr-1"/>Adicionar Aeronave</button>
              <button onClick={() => addResult('vessel')} className="bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-sm flex items-center"><PlusIcon className="h-4 w-4 mr-1"/>Adicionar Embarcação</button>
            </div>
          </div>

          <div className="space-y-6">
            {results.map((r, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">{r.entityType === 'asset' ? 'Novo Patrimônio' : r.entityType === 'vehicle' ? 'Novo Veículo' : r.entityType === 'aircraft' ? 'Nova Aeronave' : 'Nova Embarcação'}</span>
                  <button onClick={() => removeResult(idx)} className="text-red-600 text-sm flex items-center"><XMarkIcon className="h-4 w-4 mr-1"/>Remover</button>
                </div>

                {r.entityType === 'asset' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                      <input type="text" value={r.payload.description} onChange={e => updateResult(idx, 'description', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                      <select value={r.payload.type} onChange={e => updateResult(idx, 'type', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                        <option value="real_estate">Imóvel</option>
                        <option value="other">Outros</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Valor Atual</label>
                      <input type="text" value={r.display?.currentValue ?? ''} onChange={e => updateResultCurrency(idx, 'currentValue', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Data de Aquisição</label>
                      <input type="date" value={r.payload.acquisitionDate} onChange={e => updateResult(idx, 'acquisitionDate', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                  </div>
                )}

                {r.entityType === 'vehicle' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                      <input type="text" value={r.payload.make} onChange={e => updateResult(idx, 'make', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
                      <input type="text" value={r.payload.model} onChange={e => updateResult(idx, 'model', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Placa</label>
                      <input type="text" value={r.payload.licensePlate} onChange={e => updateResult(idx, 'licensePlate', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Valor de Venda</label>
                      <input type="text" value={r.display?.sellingPrice ?? ''} onChange={e => updateResultCurrency(idx, 'sellingPrice', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                  </div>
                )}

                {r.entityType === 'aircraft' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                      <input type="text" value={r.payload.make} onChange={e => updateResult(idx, 'make', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
                      <input type="text" value={r.payload.model} onChange={e => updateResult(idx, 'model', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prefixo</label>
                      <input type="text" value={r.payload.tailNumber} onChange={e => updateResult(idx, 'tailNumber', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Valor de Venda</label>
                      <input type="text" value={r.display?.sellingPrice ?? ''} onChange={e => updateResultCurrency(idx, 'sellingPrice', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                  </div>
                )}

                {r.entityType === 'vessel' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                      <input type="text" value={r.payload.make} onChange={e => updateResult(idx, 'make', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
                      <input type="text" value={r.payload.model} onChange={e => updateResult(idx, 'model', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Registro</label>
                      <input type="text" value={r.payload.registrationNumber} onChange={e => updateResult(idx, 'registrationNumber', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Valor de Venda</label>
                      <input type="text" value={r.display?.sellingPrice ?? ''} onChange={e => updateResultCurrency(idx, 'sellingPrice', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => exchangeMutation.mutate()}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center"
            >
              <ArrowRightIcon className="h-4 w-4 mr-2" /> Registrar permuta
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExchangeRegister;